import errorLogger from '@picsart/growth-rc/services/errorLogger';
import fetchWithTimeout from '@picsart/rc/fetchWithTimeout';
import { getContext } from 'configs/context';

import BatchDataService from 'pages/Batch/sections/Context/BatchDataService.ts';
import type { IProcessResult } from 'pages/Batch/utils/process/toolchain.ts';
import { BATCH_ERROR } from 'pages/Batch/configs';
import { AIBaseUrl, apiBaseUrl, maxToken } from 'configs/constants.ts';

import { ConcurrentOperation, getConcurrencyLevel } from 'pages/Batch/utils';
import PQueue from 'p-queue';
import { INPAINTING_MODEL_BODY, INPAINTING_MODEL_NEGATIVE_PROMPT } from 'pages/Batch/utils/constants.ts';
import { getPreviewUrlFromSourceUrl } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/helpers.ts';

import { ENHANCE_FORMAT, getEnhancePayload } from './utils/enhance';
import type { PluggableResult, RequestAiBackgroundOptions } from './AIBackgroundsApis/types';
import type { PipelineParams, WaitTaskParams } from './types';
import { SmartBgModel } from './types';

export class PluggableApisService {
  private concurrency = getConcurrencyLevel(ConcurrentOperation.API);

  private queue: PQueue;

  private readonly _enhanceTask = 'batch-app-enhance';

  private readonly _enhanceVer = 'v1';

  private readonly _removeBgTask = 'batch-app-bg-remove';

  private readonly _smartBgWorkflowTask = 'smart-background';

  private readonly _folderNameTask = 'batch-folder-name-completion';

  private readonly _driveCopyTask = 'batch-folder-copy';

  private readonly _smartBgImageMaxSize = 768;

  private readonly _removeBgVer = 'v2';

  private readonly _timeout = 35_000;

  private readonly _apiTimeout = 10_000;

  private readonly _delay = 200;

  private readonly _firstStepsDelay = 500;

  constructor() {
    this.queue = new PQueue({ concurrency: this.concurrency });
  }

  public async backgroundRemove(itemKey: string, isCancel: () => boolean, options?: { skipAsyncJob?: boolean }): Promise<string> {
    const sourceUrl = await PluggableApisService.getSourceUrl(itemKey);
    const params = { url: sourceUrl };
    const taskId = await this.createPipeline({ task: this._removeBgTask, version: this._removeBgVer, params });

    if (!options?.skipAsyncJob) {
      BatchDataService.updateItem(itemKey, {
        asyncJob: {
          task: this._removeBgTask,
          version: this._removeBgVer,
          taskId,
          toolAction: 'removeBackground',
        },
      });
    }

    try {
      const result = await this.waitTaskCompletion({ task: this._removeBgTask, version: this._removeBgVer, taskId, initialTimeout: 2000, isCancel });
      return result.image_url;
    } catch (err) {
      if (!options?.skipAsyncJob) {
        BatchDataService.updateItem(itemKey, { asyncJob: undefined });
      }
      throw err;
    }
  }

  public async enhanceImage(image: HTMLImageElement, itemKey: string, isCancel: () => boolean): Promise<IProcessResult> {
    const upscalePayload = getEnhancePayload(image.width, image.height);

    const sourceUrl = await PluggableApisService.getSourceUrl(itemKey);
    const params = {
      url: sourceUrl,
      format: ENHANCE_FORMAT.WEBP,
      target_scale: upscalePayload.target_scale,
      target_width: upscalePayload.target_width,
      target_height: upscalePayload.target_height,
    };

    const taskId = await this.createPipeline({ task: this._enhanceTask, version: this._enhanceVer, params });

    BatchDataService.updateItem(itemKey, {
      asyncJob: {
        task: this._enhanceTask,
        version: this._enhanceVer,
        taskId,
        toolAction: 'aiEnhance',
      },
    });

    try {
      const result = await this.waitTaskCompletion({ task: this._enhanceTask, version: this._enhanceVer, taskId, initialTimeout: 1200, isCancel });
      const resultImage = await fetch(result.url);
      const resultBlob = await resultImage.blob();

      return {
        blob: resultBlob,
        result: result.url,
      };
    } catch (err) {
      BatchDataService.updateItem(itemKey, { asyncJob: undefined });
      throw err;
    }
  }

  public async requestAiBackgrounds(count: number, isCancel: () => boolean, itemKey: string): Promise<PluggableResult[]> {
    const useWorkflowAPI = true;
    const sourceUrl = await PluggableApisService.getSourceUrl(itemKey);
    const params = {
      imageUrl: getPreviewUrlFromSourceUrl(sourceUrl, this._smartBgImageMaxSize),
      model: SmartBgModel.SMART_BG_V2_ACCELERATED,
      removeBackground: true,
      count,
    };
    const taskId = await this.createPipeline({ useWorkflowAPI, task: this._smartBgWorkflowTask, params });
    const result = await this.waitTaskCompletion({ useWorkflowAPI, task: this._smartBgWorkflowTask, taskId, initialTimeout: 3000, isCancel });
    return result.images;
  }

  public async requestAiBackground(options: RequestAiBackgroundOptions, userId: string | number, userName: string): Promise<any> {
    const { url, text, width, height, count = 4, requestNumber, negativePrompt = INPAINTING_MODEL_NEGATIVE_PROMPT } = options;
    const params = {
      prompt: text,
      batch_size: count,
      image_url: url,
      negative_prompt: negativePrompt,
      size: `${width}x${height}`,
      output_image_format: 'JPEG',
      ...INPAINTING_MODEL_BODY,
    };

    const requestOptions = {
      headers: { ...PluggableApisService.getHeaders(), username: userName, userid: userId },
      body: JSON.stringify(params),
      method: 'POST',
      timeout: this._apiTimeout,
    };
    const response = await this.fetch(`${AIBaseUrl}/stablediffusioninpaint/v1.2.1`, requestOptions, false);
    return { ...response, requestNumber };
  }

  public async requestAiPrompt(text: string, userId: string | number, userName: string): Promise<any> {
    const params = {
      prompt: text,
      max_new_tokens: maxToken,
      n_samples: 1,
      repetition_penalty: 1,
      seed: -1,
      temperature: 1,
      top_p: 1,
    };

    const requestOptions = {
      method: 'POST',
      headers: { ...PluggableApisService.getHeaders(), username: userName, userid: userId },
      body: JSON.stringify({ params }),
      timeout: this._apiTimeout,
    };

    const { response } = await this.fetch(`${apiBaseUrl}/workflows/prompt-enhancer/execute`, requestOptions, false);

    return response.result?.prompts[0] as string;
  }

  public async folderNameCompletion(urls: string[]): Promise<string> {
    const lang = getContext().general.language.getState();

    const params = {
      urls,
      lang,
    };

    const taskId = await this.createPipeline({ useWorkflowAPI: true, task: this._folderNameTask, params });
    return this.waitTaskCompletion({
      useWorkflowAPI: true,
      task: this._folderNameTask,
      taskId,
      initialTimeout: 1500,
      timeout: 5000,
      isCancel: () => false,
    });
  }

  public async copyToDrive(folderName: string, names: string[], urls: string[]): Promise<string> {
    const params = {
      folder: folderName,
      names,
      urls,
    };
    const taskId = await this.createPipeline({ useWorkflowAPI: true, task: this._driveCopyTask, params });
    return this.waitTaskCompletion({ useWorkflowAPI: true, task: this._driveCopyTask, initialTimeout: 500, taskId, isCancel: () => false });
  }

  public resumeTaskPolling(params: WaitTaskParams): Promise<any> {
    return this.waitTaskCompletion(params);
  }

  public static async getSourceUrl(itemKey: string): Promise<string> {
    const data = await BatchDataService.getData(itemKey);
    if (!data) {
      throw new Error(BATCH_ERROR.networkDisconnected);
    }
    return data.result.src || data.source.src;
  }

  private async createPipeline({ task, version, params, useWorkflowAPI = false }: PipelineParams): Promise<string> {
    const requestOptions = {
      headers: PluggableApisService.getHeaders(),
      body: JSON.stringify({ params }),
      method: 'POST',
      timeout: this._apiTimeout,
    };

    const url = useWorkflowAPI ? `${apiBaseUrl}/workflows/${task}/submit` : `${apiBaseUrl}/pluggable-api/tasks/${task}/${version}/submit`;
    const result = await this.fetch(url, requestOptions, useWorkflowAPI);
    return result.response.id;
  }

  private async fetch(url: string, requestOptions: any, useWorkflowAPI: boolean) {
    this.queue.concurrency = getConcurrencyLevel(ConcurrentOperation.API);
    return this.queue.add(async () => {
      const response = await fetchWithTimeout(url, requestOptions);
      if (!response.ok) {
        errorLogger.trigger(`Failed to create ${useWorkflowAPI ? 'Workflow' : 'Pluggable API'}  task ${response.status}: ${response.statusText}`);
        throw new Error(BATCH_ERROR.uploadFailed);
      }
      const result = await response.json();
      if (result.errorMessage) {
        errorLogger.trigger(`Failed to create ${useWorkflowAPI ? 'Workflow' : 'Pluggable API'} task ${result.errorMessage}`);
        throw new Error(BATCH_ERROR.uploadFailed);
      }
      return result;
    });
  }

  private waitTaskCompletion({ task, version, taskId, initialTimeout, isCancel, includeSubTasks, useWorkflowAPI = false, timeout = this._timeout }: WaitTaskParams): Promise<any> {
    let attempt = 1;

    const startTime = performance.now();
    const attemptFetch = (): Promise<any> => {
      const endTime = performance.now();
      const isTimeout = endTime - startTime > timeout;
      const isCancelled = isCancel();
      if (isTimeout || isCancelled) {
        errorLogger.leaveBreadcrumb(`Task ${task}=${taskId}, isCancelled=${isCancelled} did not complete within the given timeout of ${endTime - startTime}ms. or was cancelled`);
        return Promise.reject(new Error(BATCH_ERROR.networkDisconnected));
      }

      const delay = this.getDelay(initialTimeout, attempt);

      const url = useWorkflowAPI
        ? `${apiBaseUrl}/workflows/${task}/${taskId}/result${includeSubTasks ? '?includeSubTasks=true' : ''}`
        : `${apiBaseUrl}/pluggable-api/tasks/${task}/${version}/${taskId}${includeSubTasks ? '?includeSubTasks=true' : ''}`;

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          fetchWithTimeout(url, {
            method: 'GET',
            headers: PluggableApisService.getHeaders(),
            timeout: this._apiTimeout,
          })
            .then(async response => {
              const data = await response.json();
              return { data, ok: response.ok, statusText: response.statusText };
            })
            .then(response => {
              if (!response.ok) {
                throw new Error(response.data.reason || response.statusText);
              }
              return response.data;
            })
            .then(data => {
              const { status } = data.response;

              if (status === 'COMPLETED') {
                resolve(data.response.result);
              } else if (status === 'ACCEPTED') {
                attempt += 1;
                attemptFetch().then(resolve).catch(reject);
              } else {
                throw new Error(data.response.reason || status);
              }
            })
            .catch(error => {
              if (isTimeoutError(error)) {
                attempt += 1;
                return attemptFetch().then(resolve).catch(reject);
              }
              return reject(error);
            });
        }, delay);
      });
    };

    function isTimeoutError(error: any): boolean {
      return error.name === 'TimeoutError' || (error.message && error.message.includes('timeout'));
    }

    return attemptFetch();
  }

  private getDelay(initialTimeout: number, attemptNumber: number): number {
    if (attemptNumber === 1) {
      return initialTimeout + initialTimeout * 0.3 * Math.random();
    }
    if (attemptNumber <= 3) {
      return this._firstStepsDelay;
    }
    return this._delay;
  }

  public static getHeaders() {
    return {
      ...getContext().utils.api.getHeaders(),
      Accept: 'application/json',
      'Content-Type': 'application/json',
      platform: 'web',
    };
  }
}

export default new PluggableApisService();
